import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Folder,
  FileText,
  BookOpen,
  Menu,
  X,
  Plus,
  Trash,
  MoreHorizontal,
  Loader2,
  FolderTree,
  FileCode,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { cmsApi } from "@/services/api/cmsApi";
import type { CMSContent } from "@/models/CMS";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";

interface ContentTreeProps {
  onSelect: (content: CMSContent) => void;
  selectedId?: number;
}

const ContentIcons = {
  navigation: Folder,
  page: FileText,
  layout: BookOpen,
  email: BookOpen,
  component: BookOpen,
  template: FileCode,
};

export default function ContentTree({
  onSelect,
  selectedId,
}: ContentTreeProps) {
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const {
    data: contents,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["cms-content-tree"],
    queryFn: cmsApi.getContentTree,
  });

  const createContent = useMutation({
    mutationFn: ({
      parentId,
      type,
    }: {
      parentId: number | null;
      type: "page" | "template" | "navigation" | "template-folder";
    }) => {
      if (type === "template-folder") {
        return cmsApi.createTemplateFolder("New Template", parentId);
      }

      return cmsApi.createContent({
        name:
          type === "template"
            ? "New Template"
            : type === "navigation"
            ? "New Folder"
            : "New Page",
        type,
        parent_id: parentId,
        show_in_menu: false, // Set default value to false
      });
    },
    onSuccess: (newContent) => {
      queryClient.invalidateQueries({ queryKey: ["cms-content-tree"] });
      // Expand the parent node to show the new page
      if (newContent.parent_id) {
        setExpanded((prev) => ({
          ...prev,
          [newContent.parent_id]: true,
        }));
      }
      // Always expand template folders to show their structure
      if (
        newContent.type === "navigation" &&
        newContent.name?.includes("Template")
      ) {
        setExpanded((prev) => ({
          ...prev,
          [newContent.id]: true,
        }));
      }
      toast({
        title: "Success",
        description: `${
          newContent.type === "navigation"
            ? newContent.name?.includes("Template")
              ? "Template with structure"
              : "Folder"
            : "Page"
        } created successfully`,
      });
    },
  });

  const deleteContent = useMutation({
    mutationFn: (id: number) => {
      const content = contents?.find((c) => c.id === id);

      // Allow deletion of template folders (navigation items with "Template" in name)
      const isTemplateFolder =
        content?.type === "navigation" && content?.name?.includes("Template");

      // Allow deletion of template pages
      const isTemplatePage = content?.type === "template";

      // Allow deletion of regular pages
      const isPage = content?.type === "page";

      // Only prevent deletion of regular navigation folders
      if (content?.type === "navigation" && !isTemplateFolder) {
        throw new Error("Navigation folders cannot be deleted");
      }

      return cmsApi.deleteContent(id);
    },
    onSuccess: (response) => {
      // If the deleted content was selected, clear the selection
      if (selectedId === response.id) {
        onSelect(null as unknown as CMSContent);
      }

      queryClient.invalidateQueries({ queryKey: ["cms-content-tree"] });

      const deletedContent = contents?.find((c) => c.id === response.id);
      const isTemplateFolder =
        deletedContent?.type === "navigation" &&
        deletedContent?.name?.includes("Template");
      const isTemplatePage = deletedContent?.type === "template";
      const isPage = deletedContent?.type === "page";

      let successMessage = "Content deleted successfully";
      if (isTemplateFolder) {
        successMessage =
          "Template folder and all its contents deleted successfully";
      } else if (isTemplatePage) {
        successMessage = "Template page deleted successfully";
      } else if (isPage) {
        successMessage = "Page deleted successfully";
      }

      toast({
        title: "Success",
        description: successMessage,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const moveContent = useMutation({
    mutationFn: ({ id, position }: { id: number; position: number }) =>
      cmsApi.updateContent(id, { position }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms-content-tree"] });
      toast({
        title: "Success",
        description: "Page order updated successfully",
      });
    },
  });

  const updateContent = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      cmsApi.updateContent(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms-content-tree"] });
      setEditingId(null);
      toast({
        title: "Success",
        description: "Name updated successfully",
      });
    },
  });

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleContentClick = (content: CMSContent) => {
    onSelect(content);
    if (contents?.some((c) => c.parent_id === content.id)) {
      setExpanded((prev) => ({
        ...prev,
        [content.id]: true,
      }));
    }
  };

  const handleUpdateName = (id: number, newName: string) => {
    if (newName && newName.trim()) {
      updateContent.mutate({ id, name: newName.trim() });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-72 border-r h-full overflow-y-auto p-4 flex flex-col">
        <div className="text-red-500 p-4">
          Failed to load content structure: {(error as Error).message}
        </div>
      </div>
    );
  }

  if (!contents || contents.length === 0) {
    return (
      <div className="w-72 border-r h-full overflow-y-auto p-4 flex flex-col">
        <div className="flex items-center justify-between px-2 mb-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <FolderTree className="h-5 w-5" />
            <h2 className="font-medium">Content Structure</h2>
          </div>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
            <span className="sr-only">No content</span>
          </Button>
        </div>
        <div className="text-muted-foreground text-center py-8">
          No content items found
        </div>
      </div>
    );
  }

  const onDragEnd = (result: any) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const id = parseInt(draggableId);
    moveContent.mutate({
      id,
      position: destination.index,
    });
  };

  const renderTreeItems = (
    parentId: number | null = null,
    depth: number = 0
  ) => {
    // Don't filter by show_in_menu here to show all content items in the admin interface
    const items =
      contents?.filter((content) => content.parent_id === parentId) || [];

    if (items.length === 0) return null;

    return (
      <Droppable droppableId={`list-${parentId || "root"}`}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="space-y-1"
          >
            {items.map((content, index) => {
              const hasChildren = contents?.some(
                (c) => c.parent_id === content.id
              );

              const IconComponent = ContentIcons[content.type] || FileText;
              const isNavigation = content.type === "navigation";

              return (
                <Draggable
                  key={content.id}
                  draggableId={content.id.toString()}
                  index={index}
                  isDragDisabled={isNavigation}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={cn(
                        "mt-1",
                        snapshot.isDragging && "opacity-50"
                      )}
                    >
                      <div className="flex items-center group relative">
                        <div
                          {...provided.dragHandleProps}
                          className={cn(
                            "flex-1 text-left py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2",
                            "hover:bg-muted/50 group cursor-grab",
                            selectedId === content.id &&
                              "bg-muted text-brand-orange",
                            isNavigation && "text-blue-600"
                          )}
                          style={{ paddingLeft: `${12 + depth * 20}px` }}
                          onClick={() => handleContentClick(content)}
                        >
                          {hasChildren && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(content.id);
                              }}
                              className={`w-4 inline-block text-muted-foreground`}
                            >
                              {expanded[content.id] ? "▼" : "▶"}
                            </span>
                          )}

                          {!hasChildren && <span className="w-4" />}

                          {editingId === content.id ? (
                            <div
                              className="flex-1 flex items-center gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Input
                                className="h-7 text-sm"
                                defaultValue={content.name}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleUpdateName(
                                      content.id,
                                      e.currentTarget.value
                                    );
                                  } else if (e.key === "Escape") {
                                    setEditingId(null);
                                  }
                                }}
                                onBlur={(e) =>
                                  handleUpdateName(content.id, e.target.value)
                                }
                                autoFocus
                              />
                            </div>
                          ) : (
                            <>
                              <IconComponent className="h-4 w-4 flex-shrink-0" />
                              <span className="truncate flex-1">
                                {content.name}
                              </span>

                              <div className="ml-auto">
                                {(isNavigation ||
                                  content.type === "template" ||
                                  content.type === "page") && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 opacity-0 group-hover:opacity-100"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuLabel>
                                        Actions
                                      </DropdownMenuLabel>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingId(content.id);
                                        }}
                                      >
                                        Edit Name
                                      </DropdownMenuItem>
                                      {isNavigation && (
                                        <>
                                          <DropdownMenuItem
                                            onClick={() =>
                                              createContent.mutate({
                                                parentId: content.id,
                                                type: "page",
                                              })
                                            }
                                          >
                                            <Plus className="mr-2 h-4 w-4" />
                                            Add Subpage
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() =>
                                              createContent.mutate({
                                                parentId: content.id,
                                                type: "template",
                                              })
                                            }
                                          >
                                            <FileCode className="mr-2 h-4 w-4" />
                                            Add Template Page
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const isTemplateFolder =
                                            content.type === "navigation" &&
                                            content.name?.includes("Template");
                                          const isTemplatePage =
                                            content.type === "template";
                                          const isPage =
                                            content.type === "page";

                                          let confirmMessage =
                                            "Are you sure you want to delete this item?";
                                          if (isTemplateFolder) {
                                            confirmMessage =
                                              "Are you sure you want to delete this template folder? This will also delete all template pages within it.";
                                          } else if (isTemplatePage) {
                                            confirmMessage =
                                              "Are you sure you want to delete this template page?";
                                          } else if (isPage) {
                                            confirmMessage =
                                              "Are you sure you want to delete this page?";
                                          }

                                          if (window.confirm(confirmMessage)) {
                                            deleteContent.mutate(content.id);
                                          }
                                        }}
                                        className="text-red-600 focus:text-red-600"
                                      >
                                        <Trash className="mr-2 h-4 w-4" />
                                        Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {expanded[content.id] &&
                        renderTreeItems(content.id, depth + 1)}
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  };

  return (
    <div className="w-72 border-r h-full overflow-y-auto p-4 flex flex-col">
      <div className="flex items-center justify-between px-2 mb-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <FolderTree className="h-5 w-5" />
          <h2 className="font-medium">Content Structure</h2>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
              <Plus className="h-4 w-4" />
              <span className="sr-only">Add content</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Add Content</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                createContent.mutate({
                  parentId: null,
                  type: "template-folder",
                })
              }
            >
              <Folder className="mr-2 h-4 w-4" />
              Add Template
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                createContent.mutate({ parentId: null, type: "navigation" })
              }
            >
              <Folder className="mr-2 h-4 w-4" />
              Add Folder
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                createContent.mutate({ parentId: null, type: "page" })
              }
            >
              <FileText className="mr-2 h-4 w-4" />
              Add Page
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        {renderTreeItems(null)}
      </DragDropContext>
    </div>
  );
}
